<template>
  <div>
    <v-row>
      <v-col cols="12" md="12">
        <validation-provider v-slot="{ errors }" rules="required|max:255">
          <v-text-field
            id="conciliacao-nome"
            v-model="local.nome"
            label="Nome"
            outlined
            dense
            :error-messages="errors"
            @input="emitChange"
          />
        </validation-provider>
      </v-col>
    </v-row>

    <v-row>
      <v-col v-if="tipos.length" cols="12" md="4">
        <validation-provider v-slot="{ errors }" rules="required">
          <v-select
            id="conciliacao-tipo"
            v-model="local.tipo"
            :items="tipos"
            label="Tipo"
            outlined
            dense
            :error-messages="errors"
            @change="emitChange"
          />
        </validation-provider>
      </v-col>
      <v-col v-if="niveisFlex.length" cols="12" md="4">
        <v-select
          v-model="local.flexibilizacao.partida"
          :items="niveisFlex"
          :label="rotuloParametro('FLEXIBILIZACAO', 'partida', 'Flexibilização — Partida')"
          outlined
          dense
          @change="emitChange"
        />
      </v-col>
      <v-col v-if="niveisFlex.length" cols="12" md="4">
        <v-select
          v-model="local.flexibilizacao.referencia"
          :items="niveisFlex"
          :label="rotuloParametro('FLEXIBILIZACAO', 'referencia', 'Flexibilização — Referência')"
          outlined
          dense
          @change="emitChange"
        />
      </v-col>
    </v-row>

    <v-row align="center">
      <v-col v-if="parametroCoringaHabilitado" cols="12" md="4">
        <v-checkbox
          id="conciliacao-coringa"
          v-model="local.coringa.habilitado"
          :label="rotuloParametro('CORINGA', 'habilitado', 'Habilitar atributo coringa')"
          @change="onCoringaChange"
        />
      </v-col>
      <v-col v-if="niveisCoringa.length" cols="12" md="8">
        <v-select
          v-model="local.coringa.nivelCoringa"
          :items="niveisCoringa"
          :label="rotuloParametro('CORINGA', 'nivelCoringa', 'Nível do coringa')"
          outlined
          dense
          :disabled="nivelCoringaDesabilitado"
          @change="emitChange"
        />
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12" md="12" title="Nesta versão a conciliação sempre preserva a ordem original dos registros.">
        <v-checkbox
          id="conciliacao-manter-ordem-original"
          :input-value="true"
          label="Manter a ordem original dos registros"
          persistent-hint
          disabled
        />
      </v-col>
    </v-row>
  </div>
</template>

<script>
  import esquemaItensMixins from '@/mixins/conciliacoesV3/esquemaItensMixins';

  const defaultLocal = (tipoPadrao = 'STANDARD') => ({
    nome: '',
    tipo: tipoPadrao || 'STANDARD',
    flexibilizacao: { partida: null, referencia: null },
    coringa: { habilitado: false, nivelCoringa: null }
  });

  export default {
    name: 'AbaInicio',
    mixins: [esquemaItensMixins],
    props: {
      value: { type: Object, default: () => ({}) },
      esquemas: { type: Object, default: () => ({}) },
      templates: { type: Array, default: () => [] },
      tipoPadrao: { type: String, default: 'STANDARD' }
    },
    data() {
      return {
        local: defaultLocal()
      };
    },
    computed: {
      tipos() {
        return this.tiposDeTemplate(this.templates, this.tipoPadrao);
      },
      niveisFlex() {
        return this.opcoesDominio(this.parametroDoEsquema(this.esquemas, 'FLEXIBILIZACAO', 'partida')).length
          ? this.opcoesDominio(this.parametroDoEsquema(this.esquemas, 'FLEXIBILIZACAO', 'partida'))
          : this.opcoesDominio(this.parametroDoEsquema(this.esquemas, 'FLEXIBILIZACAO', 'referencia'));
      },
      niveisCoringa() {
        return this.opcoesDominio(this.parametroDoEsquema(this.esquemas, 'CORINGA', 'nivelCoringa'));
      },
      parametroCoringaHabilitado() {
        return this.parametroDoEsquema(this.esquemas, 'CORINGA', 'habilitado');
      },
      nivelCoringaDesabilitado() {
        const regra = this.parametroDoEsquema(this.esquemas, 'CORINGA', 'nivelCoringa');
        const condicao = regra?.obrigatorioSe || regra?.obrigatorio_se;
        if (!condicao) return false;
        return this.local.coringa[condicao.chave] !== condicao.valor;
      }
    },
    watch: {
      value: {
        immediate: true,
        deep: true,
        handler(val) {
          this.hidratar(val);
        }
      },
      esquemas: {
        immediate: true,
        handler() {
          this.aplicarDefaultsDoEsquema();
        }
      }
    },
    methods: {
      rotuloParametro(tipo, chave, fallback) {
        const parametro = this.parametroDoEsquema(this.esquemas, tipo, chave);
        return parametro?.rotulo || parametro?.label || fallback;
      },
      hidratar(val) {
        const base = defaultLocal(this.tipoPadrao);
        this.local = {
          ...base,
          ...(val || {}),
          tipo: val?.tipo || this.tipoPadrao || 'STANDARD',
          flexibilizacao: { ...base.flexibilizacao, ...(val?.flexibilizacao || {}) },
          coringa: { ...base.coringa, ...(val?.coringa || {}) }
        };
        this.aplicarDefaultsDoEsquema();
      },
      aplicarDefaultsDoEsquema() {
        const flexPartida = this.primeiroValor(this.parametroDoEsquema(this.esquemas, 'FLEXIBILIZACAO', 'partida'));
        const flexReferencia = this.primeiroValor(this.parametroDoEsquema(this.esquemas, 'FLEXIBILIZACAO', 'referencia'));
        const nivelCoringa = this.primeiroValor(this.parametroDoEsquema(this.esquemas, 'CORINGA', 'nivelCoringa'));

        if (!this.local.flexibilizacao.partida && flexPartida) {
          this.local.flexibilizacao.partida = flexPartida;
        }
        if (!this.local.flexibilizacao.referencia && flexReferencia) {
          this.local.flexibilizacao.referencia = flexReferencia;
        }
        if (!this.local.coringa.nivelCoringa && nivelCoringa) {
          this.local.coringa.nivelCoringa = nivelCoringa;
        }
        if (!this.local.tipo) {
          this.local.tipo = this.tipoPadrao || 'STANDARD';
        }
      },
      onCoringaChange() {
        if (!this.local.coringa.habilitado) {
          this.local.coringa.nivelCoringa = this.primeiroValor(this.parametroDoEsquema(this.esquemas, 'CORINGA', 'nivelCoringa'));
        }
        this.emitChange();
      },
      emitChange() {
        this.$emit('input', { ...this.local });
      },
      validate() {
        if (!this.local.nome || !String(this.local.nome).trim()) {
          return 'Informe o nome da conciliação.';
        }
        if (!this.local.tipo) {
          return 'Selecione o tipo.';
        }
        if (!this.local.flexibilizacao.partida || !this.local.flexibilizacao.referencia) {
          return 'Informe a flexibilização das duas bases.';
        }
        if (this.local.coringa.habilitado && !this.local.coringa.nivelCoringa) {
          return 'Selecione o nível do coringa.';
        }
        return true;
      },
      getPayload() {
        return {
          nome: this.local.nome,
          tipo: this.local.tipo || this.tipoPadrao || 'STANDARD',
          flexibilizacao: { ...this.local.flexibilizacao },
          coringa: {
            habilitado: !!this.local.coringa.habilitado,
            nivelCoringa: this.local.coringa.habilitado ? this.local.coringa.nivelCoringa : null
          }
        };
      }
    }
  };
</script>
